import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { getSmartPlatform } from '@weathernews/btos-javascript-modules/src/wniutil/common.js'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const windMaxZoom = ref(0)
const prefetchRange = 2
const isMobile = ref(false)

const addWindFlowCanvasLayer = (loadedMap, gridLayer, bounds) => {
  if (!loadedMap.getSource('wind-flow-canvas-source')) {
    loadedMap.addSource('wind-flow-canvas-source', {
      type: 'canvas',
      canvas: gridLayer.canvas,
      coordinates: [
        [bounds.getWest(), bounds.getNorth()],
        [bounds.getEast(), bounds.getNorth()],
        [bounds.getEast(), bounds.getSouth()],
        [bounds.getWest(), bounds.getSouth()]
      ],
      animate: true
    })
  }
  if (!loadedMap.getLayer('wind-flow-canvas-layer')) {
    loadedMap.addLayer({
      id: 'wind-flow-canvas-layer',
      type: 'raster',
      source: 'wind-flow-canvas-source',
      paint: {
        'raster-opacity': 1
      }
    }, '注記-copy')
  }
}

export const useWindMap = (binUrl, sliderValue, tileUrlList, options) => {
  const config = useRuntimeConfig()
  const loadedMap = shallowRef(null)
  const clickPoint = ref(null)
  const markerPosition = useCookie('markerPosition', { default: () => null })
  const tmpMarkerPosition = ref({})
  const isInheritPin = ref(false)
  const japanBounds = [
    [110.0, 15.0],
    [158.0, 50.0]
  ]
  if (import.meta.client) {
    isMobile.value = getSmartPlatform() !== ''
  }
  let gridLayer
  let customLocationControl = null
  if (import.meta.client) {
    window.addEventListener('custom-geolocate', (e) => {
      if (e && e.detail && typeof e.detail.lat === 'number' && typeof e.detail.lon === 'number') {
        clickPoint.value = { lat: e.detail.lat, lon: e.detail.lon }
      }
    })
  }

  const setWndspdLayer = () => {
    if (loadedMap.value) {
      for (let i = sliderValue.value; i <= sliderValue.value + 2; i++) {
        loadedMap.value.addSource(`wndspd${i}`, {
          type: options.type,
          tiles: [tileUrlList[i]],
          tileSize: options.tileSize,
          maxZoom: options.maxZoom,
          maxBounds: options.bounds
        })
        loadedMap.value.addLayer({
          id: `wndspd${i}`,
          type: options.type,
          source: `wndspd${i}`,
          paint: {
            'raster-resampling': 'nearest',
            'raster-opacity': i === sliderValue.value ? 1 : 0,
            'raster-opacity-transition': {
              duration: 0,
              delay: 50
            }
          }
        }, '海岸線')
      }
    }
  }
  const updateWndspdLayer = (oldValue) => {
    if (loadedMap.value) {
      const currentLayerId = `wndspd${sliderValue.value}`
      const oldLayerId = `wndspd${oldValue}`
      if (!loadedMap.value.getSource(currentLayerId)) {
        loadedMap.value.addSource(currentLayerId, {
          type: options.type,
          tiles: [tileUrlList[sliderValue.value]],
          tileSize: options.tileSize,
          maxZoom: options.maxZoom,
          maxBounds: options.bounds
        })
      }
      if (!loadedMap.value.getLayer(currentLayerId)) {
        loadedMap.value.addLayer({
          id: currentLayerId,
          type: options.type,
          source: currentLayerId,
          paint: {
            'raster-resampling': 'nearest',
            'raster-opacity': 0,
            'raster-opacity-transition': {
              duration: 0,
              delay: 50
            }
          }
        }, '海岸線')
      }
      if (loadedMap.value.getLayer(currentLayerId)) {
        loadedMap.value.setPaintProperty(currentLayerId, 'raster-opacity', 1)
      }
      if (loadedMap.value.getLayer(oldLayerId)) {
        loadedMap.value.setPaintProperty(oldLayerId, 'raster-opacity', 0)
      }
      const prev = sliderValue.value - prefetchRange < 0 ? 0 : sliderValue.value - prefetchRange
      const post = sliderValue.value + prefetchRange > tileUrlList.length - 1 ? tileUrlList.length - 1 : sliderValue.value + prefetchRange
      for (let i = prev; i <= post; i++) {
        const layerId = `wndspd${i}`
        if (!loadedMap.value.getSource(layerId)) {
          loadedMap.value.addSource(layerId, {
            type: options.type,
            tiles: [tileUrlList[i]],
            tileSize: options.tileSize,
            maxZoom: options.maxZoom,
            maxBounds: options.bounds
          })
        }
        if (!loadedMap.value.getLayer(layerId)) {
          loadedMap.value.addLayer({
            id: layerId,
            type: options.type,
            source: layerId,
            paint: {
              'raster-resampling': 'nearest',
              'raster-opacity': 0,
              'raster-opacity-transition': {
                duration: 0,
                delay: 50
              }
            }
          }, '海岸線')
        }
      }
      removeWndspdLayer(false)
    }
  }

  const removeWndspdLayer = (allFrag) => {
    if (allFrag) {
      if (loadedMap.value) {
        const prev = sliderValue.value - prefetchRange < 0 ? 0 : sliderValue.value - prefetchRange
        const post = sliderValue.value + prefetchRange > tileUrlList.length - 1 ? tileUrlList.length - 1 : sliderValue.value + prefetchRange
        for (let i = prev; i <= post; i++) {
          if (i !== sliderValue.value) {
            const layerId = `wndspd${i}`
            if (loadedMap.value.getLayer(layerId)) {
              loadedMap.value.removeLayer(layerId)
            }
            if (loadedMap.value.getSource(layerId)) {
              loadedMap.value.removeSource(layerId)
            }
          }
        }
      }
    } else {
      if (loadedMap.value) {
        if (loadedMap.value.getLayer(`wndspd${sliderValue.value - (prefetchRange + 1)}`)) {
          loadedMap.value.removeLayer(`wndspd${sliderValue.value - (prefetchRange + 1)}`)
        }
        if (loadedMap.value.getSource(`wndspd${sliderValue.value - (prefetchRange + 1)}`)) {
          loadedMap.value.removeSource(`wndspd${sliderValue.value - (prefetchRange + 1)}`)
        }
        if (loadedMap.value.getLayer(`wndspd${sliderValue.value + (prefetchRange + 1)}`)) {
          loadedMap.value.removeLayer(`wndspd${sliderValue.value + (prefetchRange + 1)}`)
        }
        if (loadedMap.value.getSource(`wndspd${sliderValue.value + (prefetchRange + 1)}`)) {
          loadedMap.value.removeSource(`wndspd${sliderValue.value + (prefetchRange + 1)}`)
        }
      }
    }
  }

  const setWindStreamLayer = async () => {
    windMaxZoom.value = options.maxzoom
    if (loadedMap.value) {
      if (gridLayer) {
        removeWindStreamLayer()
        if (typeof gridLayer.onRemove === 'function') {
          gridLayer.onRemove(loadedMap.value)
        }
        gridLayer = null
      }
      const zoom = Math.min(loadedMap.value.getZoom(), windMaxZoom.value)
      const bounds = loadedMap.value.getBounds()
      const { height, width, w, s, e, n, uData, vData } = await getWindBinData(binUrl.value, zoom, bounds)
      const GRID_INFO = {
        NLAT: n,
        SLAT: s,
        WLON: w,
        ELON: e,
        IMGW: width,
        IMGH: height
      }
      const fillData = new Float32Array(GRID_INFO.IMGW * GRID_INFO.IMGH * 4)
      uData.forEach((u, i) => {
        const v = vData[i]
        fillData[i * 4 + 0] = u
        fillData[i * 4 + 1] = v
        fillData[i * 4 + 2] = 0
        fillData[i * 4 + 3] = 0
      })
      gridLayer = new WindFlowLayer({
        id: 'gridFlowLayer',
        data: {
          data: fillData,
          width: GRID_INFO.IMGW,
          height: GRID_INFO.IMGH
        },
        particleSize: 2.6,
        GRID_INFO,
        speedFactor: 0.06,
        maxParticles: 2000
      })

      await gridLayer.onAdd(loadedMap.value)
      addWindFlowCanvasLayer(loadedMap.value, gridLayer, bounds)
    }
  }

  const updateWindStreamLayer = async () => {
    if (loadedMap.value) {
      removeWindStreamLayer()
      const zoom = Math.min(loadedMap.value.getZoom(), windMaxZoom.value)
      const bounds = loadedMap.value.getBounds()
      const { height, width, w, s, e, n, uData, vData } = await getWindBinData(binUrl.value, zoom, bounds)
      const GRID_INFO = {
        NLAT: n,
        SLAT: s,
        WLON: w,
        ELON: e,
        IMGW: width,
        IMGH: height
      }
      const fillData = new Float32Array(GRID_INFO.IMGW * GRID_INFO.IMGH * 4)
      uData.forEach((u, i) => {
        const v = vData[i]
        fillData[i * 4 + 0] = u
        fillData[i * 4 + 1] = v
        fillData[i * 4 + 2] = 0
        fillData[i * 4 + 3] = 0
      })
      if (gridLayer) {
        gridLayer.updateData(fillData, GRID_INFO)
        addWindFlowCanvasLayer(loadedMap.value, gridLayer, bounds)
      }
    }
  }

  const removeWindStreamLayer = () => {
    if (loadedMap.value) {
      if (loadedMap.value.getLayer('wind-flow-canvas-layer')) {
        loadedMap.value.removeLayer('wind-flow-canvas-layer')
      }
      if (loadedMap.value.getSource('wind-flow-canvas-source')) {
        loadedMap.value.removeSource('wind-flow-canvas-source')
      }
    }
  }

  const addLocationMarker = async (map) => {
    const image = await map.loadImage(`${config.public.imgDomain}/onebox/radar/img/icon/icon_location_pin.png`)
    map.addImage('user-location-img', image.data)
    if (!map.getSource('crosshair-vertical')) {
      map.addSource('crosshair-vertical', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: []
          }
        }
      })
    }

    if (!map.getSource('crosshair-horizontal')) {
      map.addSource('crosshair-horizontal', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: []
          }
        }
      })
    }

    if (map.getSource('crosshair-vertical') && !map.getLayer('crosshair-vertical-line')) {
      map.addLayer({
        id: 'crosshair-vertical-line',
        type: 'line',
        source: 'crosshair-vertical',
        paint: {
          'line-color': '#ffffff',
          'line-width': 1,
          'line-opacity': 0.8
        }
      })
    }

    if (map.getSource('crosshair-horizontal') && !map.getLayer('crosshair-horizontal-line')) {
      map.addLayer({
        id: 'crosshair-horizontal-line',
        type: 'line',
        source: 'crosshair-horizontal',
        paint: {
          'line-color': '#ffffff',
          'line-width': 1,
          'line-opacity': 0.8
        }
      })
    }
    map.addSource('user-location', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [] }
          }
        ]
      }
    })
    map.addLayer({
      id: 'user-location-symbol',
      type: 'symbol',
      source: 'user-location',
      layout: {
        'icon-image': 'user-location-img',
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          3,
          0.25,
          11,
          0.4
        ],
        'icon-anchor': 'bottom',
        'icon-offset': [0, 6],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true
      }
    })
  }

  const updateLocationMarker = (map, lon, lat) => {
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return
    if (map.getSource('user-location')) {
      clickPoint.value = { lon, lat }
      map.getSource('user-location').setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [lon, lat] }
          }
        ]
      })
    }
    if (map.getSource('crosshair-vertical')) {
      map.getSource('crosshair-vertical').setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: [
              [lon, japanBounds[0][1]],
              [lon, japanBounds[1][1]]
            ] }
          }
        ]
      })
    }
    if (map.getSource('crosshair-horizontal')) {
      map.getSource('crosshair-horizontal').setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: [
              [japanBounds[0][0], lat],
              [japanBounds[1][0], lat]
            ] }
          }
        ]
      })
    }
  }

  const initMap = (mapContainer, mapZoom, mapCenter, bounds) => {
    const position = useCookie('mapView', { default: () => null })
    mapCenter.value = position.value ? [position.value.lon, position.value.lat] : mapCenter.value
    return new Promise((resolve) => {
      let attributionControl = null
      const mapOptions = {
        container: mapContainer,
        style: `${config.public.siteDataDomain}/site/onebox/wind/world_style_wind.json`,
        localIdeographFontFamily: '"Noto Sans", "Noto Sans CJK SC", sans-serif',
        center: mapCenter.value,
        zoom: position.value ? position.value.zoom : mapZoom.value,
        minZoom: 3,
        maxZoom: 13,
        maxBounds: japanBounds,
        language: 'ja',
        attributionControl: false,
        dragRotate: false,
        keyboard: false,
        touchPitch: false
      }
      if (!position.value) {
        mapOptions.bounds = Array.isArray(bounds) && bounds.length > 0 ? bounds : japanBounds
      }
      const map = new maplibregl.Map(mapOptions)
      map.touchZoomRotate.disableRotation()
      const getCustomAttribution = () => {
        const features = map.queryRenderedFeatures()
        const world = features.filter((feature) => {
          return feature.layer.id === 'world_line_wo_jp'
        }).length
        const japan = features.filter((feature) => {
          return ['注記', '海岸線', '行政区域'].includes(feature.layer.id)
        }).length

        const attributions = []
        if (japan > 0) {
          attributions.push('国土地理院')
        }
        if (world > 0) {
          attributions.push('Natural Earth')
        }
        return attributions
      }

      const setCustomAttribution = () => {
        const attributions = getCustomAttribution()
        attributionControl.updateCustomAttributions(attributions)
      }

      map.once('style.load', async () => {
        attributionControl = new CustomAttributionControl({
          compact: true,
          customAttribution: getCustomAttribution()
        })
        map.addControl(attributionControl, 'bottom-left')
        customLocationControl = new CustomLocationControl(japanBounds, tmpMarkerPosition)
        map.addControl(customLocationControl, 'bottom-left')
        map.addControl(new maplibregl.NavigationControl({
          showCompass: false,
          showZoom: true
        }), 'bottom-left')

        let isTapped = null
        let touchMoved = false

        const handleTapEvent = async (e) => {
          if (isTapped) {
          // ダブルタップ（300ms以内）が行われた場合タイムアウトを解除しピンを打つなどの処理を行わない
            clearTimeout(isTapped)
            isTapped = null
            touchMoved = false
            return
          }
          isTapped = setTimeout(async () => {
            if (!touchMoved) {
            // シングルタップの処理：ピンを立てる
              updateLocationMarker(map, e.lngLat.lng, e.lngLat.lat)
              tmpMarkerPosition.value = { lat: e.lngLat.lat, lon: e.lngLat.lng }
              if (customLocationControl) {
                customLocationControl.deactivate()
              }
            }
            isTapped = null
            touchMoved = false
          }, 300)
        }

        map.on('touchmove', () => {
          touchMoved = true
        })
        if (isMobile.value) {
          map.on('touchend', handleTapEvent)
        } else {
          map.on('click', handleTapEvent)
        }

        map.on('movestart', () => {
          removeWindStreamLayer()
          removeWndspdLayer(true)
        })
        map.on('moveend', async () => {
          updateWndspdLayer()
          setCustomAttribution()
          await updateWindStreamLayer()
          mapCenter.value = [map.getCenter().lng, map.getCenter().lat]
          mapZoom.value = map.getZoom()
        })
        await addLocationMarker(map)
        if (markerPosition.value) {
          updateLocationMarker(map, markerPosition.value.lon, markerPosition.value.lat)
          tmpMarkerPosition.value = { lat: markerPosition.value.lat, lon: markerPosition.value.lon }
          markerPosition.value = null
          isInheritPin.value = true
        }
        position.value = null
        loadedMap.value = map
        resolve()
      })
      map.once('load', () => {
        setCustomAttribution()
      })
    })
  }
  const destroyMap = () => {
    if (loadedMap.value) {
      loadedMap.value.remove()
      loadedMap.value = null
    }
  }
  return {
    initMap,
    destroyMap,
    setWndspdLayer,
    updateWndspdLayer,
    setWindStreamLayer,
    updateWindStreamLayer,
    clickPoint,
    tmpMarkerPosition,
    isInheritPin,
    loadedMap
  }
}
